import { FC, useState, useMemo } from 'react'
import DOMPurify from 'isomorphic-dompurify'
import dynamic from 'next/dynamic'

import MainLayout from 'containers/main-layout/main-layout'
import WidthLimiter from 'containers/width-limiter/width-limiter'
import FiltersPC from 'components/filters/filter-pc/filter-pc'
import FiltersMobile from 'components/filters/filter-mobile/filter-mobile'
import SearchPagination from 'components/search/search-pagination/search-pagination'
const FilterCards = dynamic(() => import('components/filters/filter-cards/filter-cards'), {
  ssr: false,
})
import NewslineCard from 'components/cards/newsline-card/newsline-card'
import usePCWindow from 'hooks/usePCWindow'
import useSimpleSyncURL from '../../useSimpleSyncURL'
import useCommonSearch from '../../useCommonSearch'
import useThemes from 'store/lists/useThemes'

import { Key } from 'constants/search-data/search-data'
import { ISingleLectureSearch, IPCFilter } from 'constants/types/search.types'
import {
  InitValues,
  generateConfig,
  ContentType,
  Option,
} from 'constants/search-data/lector-single-data'
import { getData } from './utils'
import { MaterialTypes, Pathname, StatusVocabulary } from 'constants/data'
import { ScreenSize } from 'constants/media-constanst'
import { CARDS_ON_PAGE } from 'constants/search-data/search-data'
import { lectorOrigin } from 'utils/analytics/other/sub_lector/sub_lector'
import { PLACEFLAG_LECTOR_PERSONAL } from 'constants/analytic-data'

import styles from './lector-single-main.module.scss'

const SingleLectureSearch: FC<ISingleLectureSearch> = ({
  lectorData,
  pageTitle = 'Материалы лектора',
  children,
}: ISingleLectureSearch) => {
  const [isInitState, setInitState] = useState(true)

  const themes = useThemes()

  const isBigPC = usePCWindow(ScreenSize.big)
  const isMobile = usePCWindow(ScreenSize.mobile)

  const selectOptions = {
    [Key.THEMES]: themes,
  }

  /* Для единообразия с другими фильтрами */
  const selectedOption = Option.lector

  const { data, isLoading, handleParameterSet, handleReset, handlePageChange } = useCommonSearch({
    InitValues,
    selectedOption,
    getCardsMaterials: getData(lectorData.id),
    isInitState,
    selectOptions,
    setInitState,
    isMobile,
  })

  useSimpleSyncURL(data.params[selectedOption])

  const getCardType = (card: any) => {
    if (card.materialType === ContentType.WEBINAR) return MaterialTypes.WEBINAR

    return MaterialTypes.ARTICLE
  }

  const config = useMemo(() => generateConfig(themes), [themes])

  const renderedCards = useMemo(
    () =>
      data.cards[selectedOption]?.map((card: any) => {
        const cardType = getCardType(card)
        const isArticle = cardType === MaterialTypes.ARTICLE
        const linkPath = isArticle ? Pathname.ARTICLE : Pathname.WEBINAR

        return {
          url: `/${linkPath}/${card.linkTranslation || card.id}`,
          origin: PLACEFLAG_LECTOR_PERSONAL,
          item: (
            <NewslineCard
              {...card}
              materialType={cardType}
              AData={{ origin: lectorOrigin.LECTOR }}
              cutQueryWords={(data.params[selectedOption].inputText as string)
                .toLowerCase()
                .split(' ')}
            />
          ),
        }
      }) || [],
    [data.cards]
  )

  const filterPCProps: IPCFilter = {
    parameters: data.params[selectedOption],
    handleParameterSet,
    handleReset,
    config: config[selectedOption],
    isBigPC,
  }

  const filterMobileProps = {
    parameters: data.params[selectedOption],
    handleParameterSet,
    handleReset,
    config: config[selectedOption],
    allCount: data.allCount[selectedOption],
  }

  const FilterCardsProps = {
    cards: renderedCards,
    handlePageChange,
    isLoading,
  }

  return (
    <MainLayout>
      <WidthLimiter noLeftPadding noRightPadding>
        {children}
        <div className={styles.lectorSearch}>
          <h1
            className={styles.titleFirst}
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(pageTitle) }}
          ></h1>
          <div>
            <FiltersPC {...filterPCProps} />
            <FilterCards {...FilterCardsProps} />
          </div>
        </div>
        {!isLoading && (
          <SearchPagination
            maxPage={Math.ceil(data.allCount[selectedOption] / CARDS_ON_PAGE) || 0}
            currentPage={Number(data.params[selectedOption].page)}
            handleParameterSet={handleParameterSet}
          />
        )}
      </WidthLimiter>
      {!isBigPC && <FiltersMobile {...filterMobileProps} />}
    </MainLayout>
  )
}

export default SingleLectureSearch
